import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  useGetHospitalLocationsQuery,
  useGetHospitalLocationByIdQuery,
  useCreateHospitalLocationMutation,
  useUpdateHospitalLocationMutation,
} from '../../slices/hospitalLocationApiSlice';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Modal,
  Alert,
  Spinner,
  InputGroup,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import { FaEdit, FaPlus, FaSearch, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Message from '../../components/Message';

const HospitalLocationScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State for filtering
  const [filterDirectorate, setFilterDirectorate] = useState('');
  const [filterWard, setFilterWard] = useState('');
  const [uniqueDirectorates, setUniqueDirectorates] = useState([]);
  const [uniqueWards, setUniqueWards] = useState([]);

  // State for modal
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [directorateInputMode, setDirectorateInputMode] = useState('select'); // 'select' or 'new'
  const [formData, setFormData] = useState({
    costCentreCode: '',
    directorate: '',
    department: '',
    wardDepartmentArea: '',
    activeYorN: 'Yes',
  });

  // Fetch all locations
  const { 
    data: locations, 
    isLoading, 
    error, 
    refetch 
  } = useGetHospitalLocationsQuery();

  // Fetch single location for editing
  const { 
    data: selectedLocation, 
    isLoading: isLoadingSelected 
  } = useGetHospitalLocationByIdQuery(selectedLocationId, {
    skip: !selectedLocationId || modalMode !== 'edit',
  });

  // Create and update mutations
  const [createHospitalLocation, { isLoading: isCreating }] = useCreateHospitalLocationMutation();
  const [updateHospitalLocation, { isLoading: isUpdating }] = useUpdateHospitalLocationMutation();

  // Extract unique values for filters
  useEffect(() => {
    if (locations) {
      const directorates = [...new Set(locations.map(loc => loc.directorate))].sort();
      setUniqueDirectorates(directorates);

      const wards = [...new Set(locations.map(loc => loc.wardDepartmentArea))].sort();
      setUniqueWards(wards);
    }
  }, [locations]);

  // Update form when selected location is fetched
  useEffect(() => {
    if (selectedLocation && modalMode === 'edit') {
      setFormData({
        costCentreCode: selectedLocation.costCentreCode || '',
        directorate: selectedLocation.directorate || '',
        department: selectedLocation.department || '',
        wardDepartmentArea: selectedLocation.wardDepartmentArea || '',
        activeYorN: selectedLocation.activeYorN || 'Yes',
      });
      // For edit mode, always use 'select' mode and pre-fill
      setDirectorateInputMode('select');
    }
  }, [selectedLocation, modalMode]);

  // Filter locations
  const filteredLocations = locations?.filter(location => {
    const matchDirectorate = !filterDirectorate || location.directorate === filterDirectorate;
    const matchWard = !filterWard || location.wardDepartmentArea === filterWard;
    return matchDirectorate && matchWard;
  });

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      costCentreCode: '',
      directorate: '',
      department: '',
      wardDepartmentArea: '',
      activeYorN: 'Yes',
    });
    setSelectedLocationId(null);
    setDirectorateInputMode('select');
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Handle create new
  const handleCreateNew = () => {
    setModalMode('create');
    resetForm();
    setShowModal(true);
  };

  // Handle edit
  const handleEdit = (id) => {
    setModalMode('edit');
    setSelectedLocationId(id);
    setShowModal(true);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields  if (!formData.directorate || !formData.department || !formData.wardDepartmentArea) 
    if (!formData.directorate  || !formData.wardDepartmentArea) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (modalMode === 'create') {
        await createHospitalLocation(formData).unwrap();
        toast.success('Hospital location created successfully');
      } else {
        await updateHospitalLocation({
          id: selectedLocationId,
          data: formData,
        }).unwrap();
        toast.success('Hospital location updated successfully');
      }
      
      refetch();
      handleCloseModal();
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'An error occurred');
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilterDirectorate('');
    setFilterWard('');
  };

  if (isLoading) return <Loader />;
  if (error) return <Message variant='danger'>{error?.data?.message || error.message}</Message>;

  return (
    <Container>
      <Row className='mb-4'>
        <Col>
          <h1>Hospital Locations Management</h1>
        </Col>
        <Col className='text-end'>
          <Button variant='primary' onClick={handleCreateNew}>
            <FaPlus /> Create New Location
          </Button>
        </Col>
      </Row>

      {/* Filter Section */}
      <Card className='mb-4'>
        <Card.Body>
          <Card.Title>Filter Locations</Card.Title>
          <Row>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Directorate</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Select
                    value={filterDirectorate}
                    onChange={(e) => setFilterDirectorate(e.target.value)}
                  >
                    <option value=''>All Directorates</option>
                    {uniqueDirectorates.map((directorate) => (
                      <option key={directorate} value={directorate}>
                        {directorate}
                      </option>
                    ))}
                  </Form.Select>
                  {filterDirectorate && (
                    <Button 
                      variant='outline-secondary'
                      onClick={() => setFilterDirectorate('')}
                    >
                      <FaTimes />
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Ward/Department/Area</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FaSearch /></InputGroup.Text>
                  <Form.Select
                    value={filterWard}
                    onChange={(e) => setFilterWard(e.target.value)}
                  >
                    <option value=''>All Wards/Departments/Areas</option>
                    {uniqueWards.map((ward) => (
                      <option key={ward} value={ward}>
                        {ward}
                      </option>
                    ))}
                  </Form.Select>
                  {filterWard && (
                    <Button 
                      variant='outline-secondary'
                      onClick={() => setFilterWard('')}
                    >
                      <FaTimes />
                    </Button>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={2} className='d-flex align-items-end'>
              <Button 
                variant='secondary' 
                onClick={clearFilters}
                disabled={!filterDirectorate && !filterWard}
              >
                Clear Filters
              </Button>
            </Col>
          </Row>
          <Row className='mt-2'>
            <Col>
              <small className='text-muted'>
                Showing {filteredLocations?.length || 0} of {locations?.length || 0} locations
              </small>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Locations Table */}
      <Card>
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Cost Centre Code</th>
                <th>Directorate</th>

                {/*<th>Department</th> */}

                <th>Ward/Department/Area</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations?.length === 0 ? (
                <tr>
                  <td colSpan='6' className='text-center'>
                    No locations found
                  </td>
                </tr>
              ) : (
                filteredLocations?.map((location) => (
                  <tr key={location._id}>
                    <td>{location.costCentreCode || '-'}</td>
                    <td>{location.directorate}</td>

                     {/*<td>{location.department}</td> */}

                    <td>{location.wardDepartmentArea}</td>
                    <td>
                      <span className={`badge bg-${location.activeYorN === 'Yes' ? 'success' : 'danger'}`}>
                        {location.activeYorN}
                      </span>
                    </td>
                    <td>
                      <Button
                        variant='info'
                        size='sm'
                        onClick={() => handleEdit(location._id)}
                      >
                        <FaEdit /> Edit
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Create New' : 'Edit'} Hospital Location
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {(isLoadingSelected || isCreating || isUpdating) && (
              <div className='text-center mb-3'>
                <Spinner animation='border' variant='primary' />
              </div>
            )}
            
            <Row>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Cost Centre Code</Form.Label>
                  <Form.Control
                    type='text'
                    name='costCentreCode'
                    value={formData.costCentreCode}
                    onChange={handleInputChange}
                    placeholder='Enter cost centre code'
                  />
                  <Form.Text className='text-muted'>
                    Optional - for future finance integration
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Active</Form.Label>
                  <Form.Select
                    name='activeYorN'
                    value={formData.activeYorN}
                    onChange={handleInputChange}
                  >
                    <option value='Yes'>Active</option>
                    <option value='No'>Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Directorate Selection - Only show toggle for create mode */}
            {modalMode === 'create' && (
              <Form.Group className='mb-3'>
                <Form.Label>Directorate Input Method</Form.Label>
                <div>
                  <ToggleButtonGroup
                    type="radio"
                    name="directorateInputMode"
                    value={directorateInputMode}
                    onChange={(val) => setDirectorateInputMode(val)}
                  >
                    <ToggleButton 
                      id="tbg-radio-1" 
                      value={'select'}
                      variant="outline-primary"
                      size="sm"
                    >
                      Select Existing
                    </ToggleButton>
                    <ToggleButton 
                      id="tbg-radio-2" 
                      value={'new'}
                      variant="outline-primary"
                      size="sm"
                    >
                      Add New Directorate
                    </ToggleButton>
                  </ToggleButtonGroup>
                </div>
              </Form.Group>
            )}

            {/* Directorate Field */}
            <Form.Group className='mb-3'>
              <Form.Label>Directorate <span className='text-danger'>*</span></Form.Label>
              
              {modalMode === 'edit' ? (
                // Edit mode - always show as select (pre-filled with current value)
                <>
                  <Form.Select
                    name='directorate'
                    value={formData.directorate}
                    onChange={handleInputChange}
                    required
                  >
                    <option value=''>Select Directorate</option>
                    {uniqueDirectorates.map((directorate) => (
                      <option key={directorate} value={directorate}>
                        {directorate}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className='text-muted'>
                    If you need to add a new directorate, please create a new location
                  </Form.Text>
                </>
              ) : (
                // Create mode - show based on selection
                directorateInputMode === 'select' ? (
                  <Form.Select
                    name='directorate'
                    value={formData.directorate}
                    onChange={handleInputChange}
                    required
                  >
                    <option value=''>Select Directorate</option>
                    {uniqueDirectorates.map((directorate) => (
                      <option key={directorate} value={directorate}>
                        {directorate}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    type='text'
                    name='directorate'
                    value={formData.directorate}
                    onChange={handleInputChange}
                    placeholder='Enter new directorate name'
                    required
                  />
                )
              )}
            </Form.Group>

            <Row>
                {/*
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Department <span className='text-danger'>*</span></Form.Label>
                  <Form.Control
                    type='text'
                    name='department'
                    value={formData.department}
                    onChange={handleInputChange}
                    placeholder='Enter department'
                    required
                  />
                </Form.Group>
              </Col>
              */}
              
              <Col md={6}>
                <Form.Group className='mb-3'>
                  <Form.Label>Ward/Department/Area <span className='text-danger'>*</span></Form.Label>
                  <Form.Control
                    type='text'
                    name='wardDepartmentArea'
                    value={formData.wardDepartmentArea}
                    onChange={handleInputChange}
                    placeholder='Enter ward, department, or area'
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Preview of unique directorates for reference */}
            {modalMode === 'create' && directorateInputMode === 'new' && uniqueDirectorates.length > 0 && (
              <Alert variant='info' className='mt-2'>
                <small>
                  <strong>Existing directorates for reference:</strong>{' '}
                  {uniqueDirectorates.slice(0, 5).join(', ')}
                  {uniqueDirectorates.length > 5 && '...'}
                </small>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button 
              variant='primary' 
              type='submit'
              disabled={isCreating || isUpdating || isLoadingSelected}
            >
              {isCreating || isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default HospitalLocationScreen;